/** Overlay HUD for the adventure map on a landscape phone.
 *
 * The map is full-bleed. Chrome floats over it: treasury and end-turn along
 * the top, minimap top-left, hero/town thumbs on the right, status and
 * command keys along the bottom (hidden until Keys is tapped). Pointer events
 * pass through the gaps so the map underneath still takes a tap.
 */

import type { HeroId, Reason, TownId } from "@game/core";
import { useState, type ReactNode } from "react";
import { CommandBar } from "./CommandBar.js";
import { HeroList } from "./HeroList.js";
import { Minimap } from "./Minimap.js";
import { ResourceBar } from "./ResourceBar.js";
import { StatusBar } from "./StatusBar.js";
import { TownList } from "./TownList.js";
import { cx } from "../theme/components.js";
import { BitmapText } from "../theme/bitmapText.js";
import type { Hotkey } from "../hooks/useHotkeys.js";
import type { CameraView } from "./layout.js";
import type { HeroCapacities, HoverTarget, Selection, StatusState } from "./types.js";

export interface PhoneHudProps {
  readonly camera: CameraView;
  readonly onCameraChange: (camera: CameraView) => void;
  readonly selection: Selection;
  readonly onSelectHero: (id: HeroId) => void;
  readonly onSelectTown: (id: TownId) => void;
  readonly status: StatusState;
  readonly onHover?: ((target: HoverTarget) => void) | undefined;
  readonly onEndTurn: () => void;
  readonly endTurnReason?: Reason | null | undefined;
  readonly onOpenTown?: ((id: TownId) => void) | undefined;
  readonly onOpenHero?: ((id: HeroId) => void) | undefined;
  readonly capacities?: HeroCapacities | undefined;
  readonly conditionSubject?: string | null | undefined;
  readonly hotkeys: readonly Hotkey[];
  readonly leading?: ReactNode | undefined;
  /**
   * Opens the right-press card for the armed destination. A thumb has no
   * right button; the long press that stands in for one is a gesture nobody
   * is told about, so the same card is one visible key away.
   */
  readonly onInfoKey?: (() => void) | undefined;
  readonly infoOpen?: boolean | undefined;
}

export function PhoneHud(props: PhoneHudProps): JSX.Element {
  const [keysOpen, setKeysOpen] = useState(false);
  return (
    <div className={cx("hm-phone", keysOpen && "hm-phone--keys")} aria-label="Adventure controls">
      <div className="hm-phone__top">
        <ResourceBar
          onEndTurn={props.onEndTurn}
          endTurnReason={props.endTurnReason ?? null}
          conditionSubject={props.conditionSubject ?? null}
          leading={props.leading}
        />
      </div>
      <div className="hm-phone__minimap">
        <Minimap camera={props.camera} onCameraChange={props.onCameraChange} />
        <div className="hm-phone__zoom">
          <button
            type="button"
            className="hm-phone__zoom-btn"
            aria-label="Zoom out"
            onClick={() => props.hotkeys.find((h) => h.combo === "-")?.onFire()}
          >
            −
          </button>
          <button
            type="button"
            className="hm-phone__zoom-btn"
            aria-label="Zoom in"
            onClick={() => props.hotkeys.find((h) => h.combo === "=")?.onFire()}
          >
            +
          </button>
        </div>
      </div>
      <div className="hm-phone__rail">
        <HeroList
          className="hm-phone__heroes"
          selectedHeroId={props.selection.heroId}
          onSelect={props.onSelectHero}
          onOpen={props.onOpenHero}
          onHover={props.onHover}
          capacities={props.capacities}
        />
        <TownList
          className="hm-phone__towns"
          selectedTownId={props.selection.townId}
          onSelect={props.onSelectTown}
          onOpen={props.onOpenTown}
          onHover={props.onHover}
        />
      </div>
      <div className="hm-phone__bottom">
        {/* The reading line, drawn with the keys and hidden with them.
            It says what is under the finger and what the selected hero is
            standing on - the object, and the ground beneath it - which on a
            desktop is read off the sidebar the phone does not have.

            It was briefly always on, because when the keys were shut there was
            no other way to learn what a hero was about to walk onto. The Info
            key IS that other way, so the plate went back to travelling with
            the panel: Hide now clears the whole bottom edge and gives the map
            the screen, which is the only reason a phone player taps Hide. */}
        {!keysOpen ? null : (
          <StatusBar
            status={props.status}
            selectedHeroId={props.selection.heroId}
            capacities={props.capacities}
          />
        )}
        {/* The keys and the toggle share the bottom line and are pushed into
            its two corners: a thumb reaches a corner of a phone without moving
            the hand, and the middle of the bottom edge is where the map is
            being looked at. */}
        <div className="hm-phone__keysrow">
          <div id="hm-phone-keys" className="hm-phone__keyscluster" hidden={!keysOpen}>
            <CommandBar hotkeys={props.hotkeys} />
          </div>
          <button
            type="button"
            className="hm-phone__keys-toggle"
            aria-expanded={keysOpen}
            aria-controls="hm-phone-keys"
            onClick={() => setKeysOpen((o) => !o)}
          >
            <BitmapText face="roman" scale={1}>{keysOpen ? "Hide" : "Keys"}</BitmapText>
          </button>
          {/* The Info key sits beside Keys in the same corner: what the last
              tap aimed at, or what the hero stands on, read as the card a
              right press gives a desktop. */}
          {props.onInfoKey === undefined ? null : (
            <button
              type="button"
              className="hm-phone__info-key"
              aria-label="Info"
              aria-pressed={props.infoOpen === true}
              onClick={props.onInfoKey}
            >
              <BitmapText face="roman" scale={1}>Info</BitmapText>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
